import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { DispatchType } from "../../interfaces/redux.interface";
import { startLoading, stopLoading } from "../../redux/slices/WindowSlice";
import { successToast } from "../../utils/toast.util";
import {
  BasicFeesType,
  BoardOfStudentType,
  CertificationType,
  CityType,
  ExperienceType,
  FeesCalculatorInterface,
  HQLevelsType,
  LevelOfStudentType,
  PerKMRateAsPerTierType,
  RatingType,
} from "../../interfaces/info.interface";
import { get, put } from "../../redux/ApiHelper";

// Default initial values matching the interface
const initialFeesValues: FeesCalculatorInterface = {
  basicFees: {
    ALL_SUBJECT: 80,
    SCIENCE_GROUP: 80,
    HUMANITIES_GROUP: 80,
    COMMERCIAL_STUDIES: 80,
    INDIAN_LANGUAGE: 80,
    FOREIGN_LANGUAGES: 80,
    OTHER: 80,
  },
  hqLevels: {
    PRE_MATRIC: 0,
    MATRIC: 15,
    POST_MATRIC: 25,
    GRADUATE: 35,
    POST_GRADUATE: 45,
    PHD: 50,
  },
  noOfYearOfExperience: {
    FRESHER: 0,
    ONE_TO_TWO_YEARS: 15,
    THREE_TO_FIVE_YEARS: 20,
    FIVE_TO_EIGHT_YEARS: 25,
    EIGHT_TO_TEN_YEARS: 30,
    TEN_TO_FIFTEEN_YEARS: 40,
    ABOVE: 50,
  },
  rating: {
    r_5: 75,
    r_4Point9To4Point75: 60,
    r_4Point74To4Point5: 50,
    r_4Point4To4Point0: 45,
    r_4Point9To3Point5: 30,
    r_3Point4To3Point0: 25,
    r_2Point9To2Point5: 0,
    r_2Point4To2Point0: -25,
    r_2Point0To1Point0: -30,
    r_lessThan1: -75,
  },
  city: { TIER1: 15, TIER2: 10, TIER3: 0, SUB_URBAN: -5, RURAL: -10 },
  boardOfstudent: { CBSE: 10, ICSE: 15, IR: 0, State: 0, IGCSE: 0, IB: 20 },
  level: {
    l_PRE_PRIMARY: 0,
    l_PRIMARY_LOWER: 10,
    l_PRIMARY_UPPER: 15,
    l_MIDDLE_SCHOOL: 20,
    l_SECONDARY: 25,
    l_SENIOR_SECONDARY: 30,
  },
  perKMRateAsPerTier: {
    TIER1: 8,
    TIER2: 7,
    TIER3: 6,
    SUB_URBAN: 5,
    RURAL: 4,
  },
  certification: {
    c_90_TO_100: 10,
    c_80_TO_89: 8,
    c_70_TO_79: 6,
    c_60_TO_69: 4,
    c_50_TO_59: 2,
    c_40_TO_49: 0,
    c_30_TO_39: -2,
    c_20_TO_29: -4,
    c_10_TO_19: -6,
    c_0_TO_9: -8,
  },
};

const FeesCalculatorInfoSettings: React.FC = () => {
  const dispatch: DispatchType = useDispatch();
  const [feesInfo, setFeesInfo] =
    useState<FeesCalculatorInterface>(initialFeesValues);
  const [formValues, setFormValues] =
    useState<FeesCalculatorInterface>(initialFeesValues);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch existing settings
  useEffect(() => {
    dispatch(startLoading());
    const getFeesInfo = async () => {
      try {
        const res = await get("/admin/fees-calculator/get-info");
        setFormValues(res.feesInfo);
        setFeesInfo(res.feesInfo);
      } catch (error) {
        throw error;
      } finally {
        dispatch(stopLoading());
      }
    };
    getFeesInfo();
  }, []);

  // Enable/disable submit
  useEffect(() => {
    setIsDisabled(JSON.stringify(formValues) === JSON.stringify(feesInfo));
  }, [formValues, feesInfo]);

  const handleTopLevelChange = <K extends keyof FeesCalculatorInterface>(
    section: K,
    key: keyof FeesCalculatorInterface[K],
    value: number
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value } as any,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdating(true);
    try {
      const res = await put("/admin/fees-calculator/update", formValues);
      if (res.message) {
        successToast(res.message);
      }
    } catch (error) {
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="bg-gray-900 text-yellow-500 flex items-center justify-center p-4 flex-grow">
      <div className="bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-3xl overflow-auto">
        <h1 className="text-xl font-semibold mb-4">Fees Calculator Settings</h1>
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Fees Section */}
          <div>
            <h2 className="text-lg font-semibold mb-2">Basic Fees</h2>

            <div className="grid grid-cols-2 gap-4">
              {Object.keys(initialFeesValues.basicFees as object).map((k) => (
                <label key={k} className="block text-left">
                  {k} in (INR):
                  <input
                    type="number"
                    value={formValues.basicFees[k as keyof BasicFeesType]}
                    onChange={(e) =>
                      handleTopLevelChange(
                        "basicFees",
                        k as any,
                        +e.target.value
                      )
                    }
                    className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:ring-yellow-500"
                  />
                </label>
              ))}
            </div>
          </div>
          {/* per km rate as tier
           */}
          <div>
            <h2 className="text-lg font-semibold mb-2">
              Per KM rate as per tier
            </h2>
            <div className="grid grid-cols-3 gap-4">
              {Object.keys(initialFeesValues.perKMRateAsPerTier).map((k) => (
                <label key={k} className="block text-left">
                  {k} (INR):
                  <input
                    type="number"
                    value={
                      formValues.perKMRateAsPerTier[
                      k as keyof PerKMRateAsPerTierType
                      ]
                    }
                    onChange={(e) =>
                      handleTopLevelChange(
                        "perKMRateAsPerTier",
                        k as any,
                        +e.target.value
                      )
                    }
                    className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:ring-yellow-500"
                  />
                </label>
              ))}
            </div>
          </div>
          {/* Highest Degree Section */}
          <div>
            <h2 className="text-lg font-semibold mb-2">
              Highest Qualification Levels
            </h2>
            <div className="grid grid-cols-3 gap-4">
              {Object.keys(initialFeesValues.hqLevels).map((k) => (
                <label key={k} className="block text-left">
                  {k} (%):
                  <input
                    type="number"
                    value={formValues.hqLevels[k as keyof HQLevelsType]}
                    onChange={(e) =>
                      handleTopLevelChange(
                        "hqLevels",
                        k as any,
                        +e.target.value
                      )
                    }
                    className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:ring-yellow-500"
                  />
                </label>
              ))}
            </div>
          </div>

          {/* Years of Experience Section */}
          <div>
            <h2 className="text-lg font-semibold mb-2">Years of Experience</h2>
            <div className="grid grid-cols-3 gap-4">
              {Object.keys(initialFeesValues.noOfYearOfExperience).map((k) => (
                <label key={k} className="block text-left">
                  {convertExperienceKeyToLabel(k)} (%):
                  <input
                    type="number"
                    value={
                      formValues.noOfYearOfExperience[k as keyof ExperienceType]
                    }
                    onChange={(e) =>
                      handleTopLevelChange(
                        "noOfYearOfExperience",
                        k as any,
                        +e.target.value
                      )
                    }
                    className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:ring-yellow-500"
                  />
                </label>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-lg font-semibold mb-2">Level</h2>
            <div className="grid grid-cols-3 gap-4">
              {Object.keys(initialFeesValues.level).map((k) => (
                <label key={k} className="block text-left">
                  {getReadableLevel(k)} (%):
                  <input
                    type="number"
                    value={formValues.level[k as keyof LevelOfStudentType]}
                    onChange={(e) =>
                      handleTopLevelChange("level", k as any, +e.target.value)
                    }
                    className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:ring-yellow-500"
                  />
                </label>
              ))}
            </div>
          </div>

          {/* Rating Section */}
          <div>
            <h2 className="text-lg font-semibold mb-2">Rating</h2>
            <div className="grid grid-cols-2 gap-4">
              {Object.keys(initialFeesValues.rating).map((k) => (
                <label key={k} className="block text-left">
                  {convertKeyToLabel(k)} (%):
                  <input
                    type="number"
                    value={formValues.rating[k as keyof RatingType]}
                    onChange={(e) =>
                      handleTopLevelChange("rating", k as any, +e.target.value)
                    }
                    className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:ring-yellow-500"
                  />
                </label>
              ))}
            </div>
          </div>

          {/* City Section */}
          <div>
            <h2 className="text-lg font-semibold mb-2">City</h2>
            <div className="grid grid-cols-2 gap-4">
              {Object.keys(initialFeesValues.city).map((k) => (
                <label key={k} className="block text-left">
                  {k} (%):
                  <input
                    type="number"
                    value={formValues.city[k as keyof CityType]}
                    onChange={(e) =>
                      handleTopLevelChange("city", k as any, +e.target.value)
                    }
                    className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:ring-yellow-500"
                  />
                </label>
              ))}
            </div>
          </div>

          {/* Board Of Student Section */}
          <div>
            <h2 className="text-lg font-semibold mb-2">Board of Student</h2>
            <div className="grid grid-cols-3 gap-4">
              {Object.keys(initialFeesValues.boardOfstudent).map((k) => (
                <label key={k} className="block text-left">
                  {k} (%):
                  <input
                    type="number"
                    value={
                      formValues.boardOfstudent[k as keyof BoardOfStudentType]
                    }
                    onChange={(e) =>
                      handleTopLevelChange(
                        "boardOfstudent",
                        k as any,
                        +e.target.value
                      )
                    }
                    className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:ring-yellow-500"
                  />
                </label>
              ))}
            </div>
          </div>

          {/* Certification Section */}
          <div>
            <h2 className="text-lg font-semibold mb-2">Certification</h2>
            <div className="grid grid-cols-3 gap-4">
              {Object.keys(initialFeesValues.certification).map((k) => (
                <label key={k} className="block text-left">
                  {convertCertificationKeyToLabel(k)} (%):
                  <input
                    type="number"
                    value={
                      formValues.certification[k as keyof CertificationType]
                    }
                    onChange={(e) =>
                      handleTopLevelChange(
                        "certification",
                        k as any,
                        +e.target.value
                      )
                    }
                    className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:ring-yellow-500"
                  />
                </label>
              ))}
            </div>
          </div>
          <button
            type="submit"
            className={`w-full py-2 px-4 rounded focus:outline-none focus:ring-2 ${isDisabled
              ? "bg-yellow-500 opacity-50 hover:bg-yellow-500 text-gray-300 cursor-not-allowed"
              : "bg-yellow-500 text-gray-900 hover:bg-yellow-400 focus:ring-yellow-500"
              }`}
            disabled={isDisabled}
          >
            {isUpdating ? "Updating..." : "Update Settings"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FeesCalculatorInfoSettings;

function convertKeyToLabel(key: string): string {
  if (key === "r_5") return "5.0";
  if (key === "r_lessThan1") return "Less than 1.0";

  // Remove the 'r_' prefix
  const cleanKey = key.replace(/^r_/, "");

  // Replace "Point" with "." and split on "To"
  const parts = cleanKey
    .replace(/Point/g, ".")
    .split("To")
    .map((p) => parseFloat(p).toFixed(2));

  return `${parts[0]} to ${parts[1]}`;
}

function convertExperienceKeyToLabel(key: string): string {
  switch (key) {
    case "FRESHER":
      return "Fresher";
    case "ABOVE":
      return "Above 15 Years";
    default:
      return (
        key
          .toLowerCase()
          .replace(/_/g, " ")
          .replace(
            /\b(one|two|three|four|five|eight|ten|fifteen)\b/g,
            (word) => {
              const numbers: Record<string, string> = {
                one: "1",
                two: "2",
                three: "3",
                four: "4",
                five: "5",
                eight: "8",
                ten: "10",
                fifteen: "15",
              };
              return numbers[word];
            }
          )
          .replace(/\b(\d+) (\d+)\b/, "$1 to $2") + " Years"
      );
  }
}

export function getReadableLevel(label: string): string {
  return label
    .replace(/^l_/, "") // Remove the 'l_' prefix
    .replace(/_/g, " ") // Replace underscores with spaces
    .replace(/\bPRE PRIMARY\b/, "Pre-Primary")
    .replace(/\bPRIMARY LOWER\b/, "Primary (Lower)")
    .replace(/\bPRIMARY UPPER\b/, "Primary (Upper)")
    .replace(/\bMIDDLE SCHOOL\b/, "Middle School")
    .replace(/\bSECONDARY\b/, "Secondary")
    .replace(/\bSENIOR SECONDARY\b/, "Senior Secondary")
    .trim();
}

function convertCertificationKeyToLabel(key: string): string {
  // Remove the 'c_' prefix and replace '_' with ' to '
  const cleanKey = key.replace(/^c_/, "").replace(/_/g, " to ");
  return cleanKey;
}
